import React, { memo, useRef, useState, useContext, useCallback } from 'react';
import { Icon, Modal, Radios, Spacer, Button, Flexer } from '@ontruck/orc';
import { StyledActionsMenu } from 'modules/assignment-cards/styled';
import ReactTooltip from 'react-tooltip';
import classnames from 'classnames';
import PrebookingAssignmentContext from '../context';

const t = {
  menuCallCarrier: 'Call Carrier',
  menuCancelBooking: 'Cancel pre-booking',
  moveToOccupied: 'Move to occupied',
  moveToUnOccupied: 'Move to unoccupied',
  modalHeader: 'Why are you canceling this pre-booking?',
  modalPrimary: 'Cancel pre-booking',
  modalSecondary: 'Do not cancel',
  modalQuestions: [
    {
      value: 'CANCEL_NORMAL',
      label: `The driver cancels with time and it doesn't cause us a problem`,
    },
    {
      value: 'CANCEL_DRIVER_FAULT',
      label: `Driver's fault. The driver cancels with short notice or doesn't want to do the jobs`,
    },
    {
      value: 'CANCEL_ONTRUCK_FAULT',
      label: `Ontruck's fault. There are no jobs or the jobs don’t fit with the vehicle or location.`,
    },
  ],
};

const CardActions = memo(({ id }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const tooltipRef = useRef(null);

  const { cancelPrebooking, prebookingsObject, completePrebooking, prebookingFilters } = useContext(
    PrebookingAssignmentContext
  );

  const { active_at = '' } = prebookingFilters || {};
  const prebooking = prebookingsObject[id] || {};

  const { is_completed } = prebooking;

  const handleAfterShow = useCallback(() => setMenuOpen(true), []);
  const handleafterHide = useCallback(() => setMenuOpen(false), []);
  const handleOpenModal = useCallback(() => {
    const current = tooltipRef.current;
    current.tooltipRef = null;
    ReactTooltip.hide();
    setModalOpen(true);
  }, []);

  const handleAnswerChange = useCallback((e, { value }) => {
    setAnswer(value);
  }, []);

  const handleSecondaryModal = useCallback(() => {
    setAnswer(null);
    setModalOpen(false);
  }, []);

  const handleCancelPrebooking = useCallback(() => {
    cancelPrebooking({
      prebooking_accepted_slot: id,
      reason: answer,
      start_date: active_at,
      end_date: active_at,
    });
    setModalOpen(false);
    setAnswer(null);
  }, [cancelPrebooking, answer, id, active_at]);

  const handleCompletePrebooking = useCallback(async () => {
    try {
      await completePrebooking(
        {
          id,
          start_date: active_at,
          end_date: active_at,
        },
        true
      );
    } catch (err) {
      console.error(err);
    } finally {
      ReactTooltip.hide();
    }
  }, [id, completePrebooking, active_at]);

  const handleUncompletePrebooking = useCallback(async () => {
    try {
      await completePrebooking(
        {
          id,
          start_date: active_at,
          end_date: active_at,
        },
        false
      );
    } catch (err) {
      console.error(err);
    } finally {
      ReactTooltip.hide();
    }
  }, [id, completePrebooking, active_at]);

  return (
    <StyledActionsMenu>
      <button
        className={classnames('opener', { 'opener--active': isMenuOpen })}
        data-testid="prebooking-actions-menu"
        data-tip
        data-event="click"
        data-iscapture="true"
        data-for={`card-actions-${id}`}
      >
        <Icon glyph="more-actions" />
      </button>
      <ReactTooltip
        ref={tooltipRef}
        className="tooltip"
        id={`card-actions-${id}`}
        type="light"
        effect="solid"
        place="top"
        clickable
        scrollHide={true}
        afterShow={handleAfterShow}
        afterHide={handleafterHide}
      >
        <ul className="menu">
          <li className="menu-item">
            <button
              className="menu-button"
              onClick={is_completed ? handleUncompletePrebooking : handleCompletePrebooking}
            >
              {is_completed ? t.moveToUnOccupied : t.moveToOccupied}
            </button>
          </li>
          <li className="menu-item">
            <button className="menu-button" onClick={handleOpenModal}>
              {t.menuCancelBooking}
            </button>
          </li>
        </ul>
      </ReactTooltip>

      <Modal show={isModalOpen} headerComponent={t.modalHeader} closeButton={false}>
        <Spacer mBottom="S">
          <Radios
            legend={''}
            size="M"
            name="prebooking-cancel-reason"
            items={t.modalQuestions}
            onChange={handleAnswerChange}
          />
        </Spacer>
        <Flexer.Parent justifyContent="flex-end">
          <Spacer mRight="S">
            <Button
              onClick={handleSecondaryModal}
              nude
              skin="secondary"
              size="M"
              text={t.modalSecondary}
            />
          </Spacer>
          <Button
            disabled={!answer}
            skin="primary"
            size="M"
            text={t.modalPrimary}
            onClick={handleCancelPrebooking}
          />
        </Flexer.Parent>
      </Modal>
    </StyledActionsMenu>
  );
});

export default CardActions;
